import React, { useCallback, useEffect, useState } from "react";
import { Alert, DeviceEventEmitter, Platform, Text } from "react-native";
import * as Linking from "expo-linking";
import tw from "twrnc";

import analytics from "@/analytics";
import EventSettingsScreenContainer from "@/components/EventSettingsScreenContainer";
import Button from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { TextInputBox } from "@/components/settings/Field";
import {
  SettingsPage,
  SettingsSection,
} from "@/components/settings/SettingsPage";
import { colors, text } from "@/components/settings/theme";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import { deleteEvent } from "@/components/http/events";
import { notify } from "@/components/shared/AppToast";
import DataFileRow from "@/components/library/DataFileRow";
import RecordingRow from "@/components/library/RecordingRow";
import { libraryAvailableFor } from "@/components/library/config";
import {
  DATA_FILES,
  PAGE_SIZE,
  countLabel,
  recordingTitle,
} from "@/components/library/logic";
import useLibrary from "@/components/library/useLibrary";
import { numberLocale, t, tn } from "@/localization";
import { useAuth } from "../../AuthContext";

// Profile › Download everything: every recording of the account, each with
// its own download link (one MP4, every live session and upload joined in
// order), and the account's data as separate files. Business accounts, behind
// EXPO_PUBLIC_DOWNLOAD_EVERYTHING (components/library/config.js).

const SEARCH_FROM = 6; // recordings before the search box shows

// Archived recordings (the Secure Archive) are never downloaded here. They
// offer Restore, the paid restore the events list's Restore opens (evt.live
// /api/payRestore, a Stripe checkout), and Delete, which deletes the event
// the way the event menu's Delete Event does.
function openRestore(rec, url) {
  analytics.track("Library Restore Clicked");
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  Linking.openURL(url).catch(() => {});
}

function useDeleteArchived(onDeleted) {
  const { refetchProfile } = useAuth();
  const { showActionSheetWithOptions } = useActionSheet();
  const [deleting, setDeleting] = useState(null);

  const run = useCallback(
    async (rec) => {
      setDeleting(rec.slug);
      try {
        await deleteEvent(rec.slug);
        DeviceEventEmitter.emit("event_delete", rec.slug);
        analytics.track("Library Archived Deleted");
        onDeleted(rec.slug);
        notify.success(t("Deleted"), recordingTitle(rec));
        Promise.resolve(refetchProfile?.()).catch(() => {});
      } catch (err) {
        notify.error(
          t("Couldn't delete it"),
          err?.errors?.[0]?.message || err?.message || t("Please try again.")
        );
      } finally {
        setDeleting(null);
      }
    },
    [onDeleted, refetchProfile]
  );

  const ask = useCallback(
    (rec) => {
      if (deleting) return;
      const title = t("Delete “{title}”?", { title: recordingTitle(rec) });
      const message = t(
        "The event, its recording, guest list and guestbook are deleted for good. This can't be undone."
      );
      if (Platform.OS === "ios" || Platform.OS === "android") {
        Alert.alert(
          title,
          message,
          [
            { text: t("Cancel"), style: "cancel" },
            {
              text: t("Delete event"),
              style: "destructive",
              onPress: () => void run(rec),
            },
          ],
          { cancelable: true }
        );
        return;
      }
      showActionSheetWithOptions(
        {
          options: [t("Delete event"), t("Cancel")],
          cancelButtonIndex: 1,
          destructiveButtonIndex: 0,
          title,
          message,
        },
        (buttonIndex) => {
          if (buttonIndex === 0) void run(rec);
        }
      );
    },
    [deleting, run, showActionSheetWithOptions]
  );

  return { deleting, ask };
}

// Re-renders the rows once a minute, so "Preparing…" can say it's taking
// longer than usual.
function useMinuteClock(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

const DownloadEverything = ({ route }) => {
  const source = route?.params?.source || "link";
  const { profileLoaded, account_type, broadcaster, username } = useAuth();
  const owner = broadcaster || username;
  const available = libraryAvailableFor(account_type);
  const lib = useLibrary(profileLoaded && available ? owner : null);
  const archive = useDeleteArchived(lib.remove);
  const preparing = Object.values(lib.downloads).some(
    (d) => d?.status === "preparing"
  );
  const now = useMinuteClock(preparing);

  useEffect(() => {
    if (profileLoaded && available)
      analytics.track("Library Viewed", { source });
  }, [profileLoaded, available, source]);

  const openPdf = useCallback(
    (rec) => lib.openFile("guestbook_pdf", rec.slug),
    [lib.openFile]
  );

  const recordingsDescription = lib.loading
    ? t("Loading your recordings…")
    : lib.error || !lib.total
    ? null
    : [
        countLabel(lib.total),
        t("one MP4 file each"),
        lib.complete ? null : t("loading more…"),
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <EventSettingsScreenContainer
      fallbackRoute="Profile"
      loading={!profileLoaded || (available && lib.loading)}
    >
      <SettingsPage
        title={t("Download everything")}
        description={
          <>
            <Text style={{ color: colors.ink, fontWeight: "600" }}>
              {t(
                "Your recordings are yours. Download any of them, any time, free."
              )}
            </Text>
            {"\n"}
            {t(
              "Give a family or a couple their video, or keep your own archive."
            )}
          </>
        }
      >
        {profileLoaded && !available ? (
          <Callout tone="info">
            {t(
              "Download everything is for business accounts. To download a video, open its event and choose Download & View Stats."
            )}
          </Callout>
        ) : (
          <>
            <SettingsSection
              first
              title={t("Recordings")}
              description={recordingsDescription}
            >
              {!!lib.error && (
                <Callout
                  tone="danger"
                  title={t("Couldn't load your recordings")}
                  action={
                    <Button
                      text={t("Try again")}
                      size="sm"
                      fullWidth={false}
                      icon="refresh"
                      onPress={lib.reload}
                    />
                  }
                >
                  {lib.error}
                </Callout>
              )}

              {!lib.loading && !lib.error && lib.total === 0 && (
                <Callout
                  tone="neutral"
                  icon="videocam-outline"
                  title={t("No recordings yet")}
                >
                  {t(
                    "When you stream an event or upload a video, it shows up here, ready to download."
                  )}
                </Callout>
              )}

              {lib.total >= SEARCH_FROM && (
                <TextInputBox
                  value={lib.query}
                  onChangeText={lib.search}
                  placeholder={t("Search for a recording")}
                  accessibilityLabel={t(
                    "Search recordings by title, place or date"
                  )}
                  autoCorrect={false}
                  autoCapitalize="none"
                  returnKeyType="search"
                  style={tw`mb-3`}
                  actions={
                    lib.query
                      ? [
                          {
                            icon: "close-circle",
                            label: t("Clear search"),
                            onPress: () => lib.search(""),
                          },
                        ]
                      : null
                  }
                />
              )}

              {!!lib.query && lib.filteredCount === 0 && (
                <Text style={[text.hint, tw`py-3`]}>
                  {t("No recordings match “{query}”.", {
                    query: lib.query.trim(),
                  })}
                </Text>
              )}

              {lib.visible.map((rec, i) => (
                <RecordingRow
                  key={rec.slug}
                  first={i === 0}
                  rec={rec}
                  now={now}
                  state={lib.downloads[rec.slug]}
                  pdfState={lib.files[`guestbook_pdf:${rec.slug}`]}
                  deleting={archive.deleting === rec.slug}
                  onDownload={lib.download}
                  onDelete={archive.ask}
                  onRestore={openRestore}
                  onGuestbookPdf={openPdf}
                />
              ))}

              {lib.hasMore && (
                <Button
                  text={t("Show {count} more", {
                    count: Math.min(
                      PAGE_SIZE,
                      lib.filteredCount - lib.visible.length
                    ),
                  })}
                  variant="tertiary"
                  size="md"
                  onPress={lib.showMore}
                  style={tw`mt-2 self-center`}
                />
              )}

              {lib.visible.length > 0 && (
                <Text style={[text.hint, tw`mt-3`]}>
                  {lib.query
                    ? `${tn(
                        "{count} recording found.",
                        "{count} recordings found.",
                        lib.filteredCount,
                        { count: lib.filteredCount.toLocaleString(numberLocale()) }
                      )} `
                    : ""}
                  {t(
                    "Each MP4 joins every live session and upload in order, in the best quality we keep."
                  )}{" "}
                  {Platform.OS === "web"
                    ? t(
                        "Long recordings are big files, so use a fast connection."
                      )
                    : t(
                        "It opens in your browser to save. Long recordings are big files: a computer on Wi-Fi is quickest."
                      )}
                </Text>
              )}
            </SettingsSection>

            <SettingsSection
              title={t("Your data")}
              description={t(
                "Spreadsheets for Excel, Numbers or Google Sheets, covering every event in your account"
              )}
            >
              {DATA_FILES.map((item, i) => (
                <DataFileRow
                  key={item.file}
                  first={i === 0}
                  item={item}
                  state={lib.files[item.file]}
                  onDownload={lib.openFile}
                />
              ))}
            </SettingsSection>
          </>
        )}
      </SettingsPage>
    </EventSettingsScreenContainer>
  );
};

export default DownloadEverything;
